import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { LuArrowUpRight } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";
import { getSetting } from "../../queries/setting";

const RobotSection = () => {
  const queryClient = useQueryClient();
  const [credentialsDraft, setCredentialsDraft] = useState({});
  const [editingPassword, setEditingPassword] = useState(false);

  const { data: setting } = useQuery({ queryKey: ["setting"], queryFn: getSetting });

  const settingMutation = useMutation({
    mutationFn: (patch) => API.post("/setting", patch),
    onSuccess: ({ data }) => queryClient.setQueryData(["setting"], data),
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur d'enregistrement");
    },
  });

  if (!setting) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
      </div>
    );
  }

  const credentials = setting.webAppCredentials;
  const changedCredentials = Object.fromEntries(Object.entries(credentialsDraft).filter(([key, value]) => (key === "password" ? value.length > 0 : value !== credentials[key])));

  const onSaveCredentials = (e) => {
    e.preventDefault();
    settingMutation.mutate(
      { webAppCredentials: changedCredentials },
      {
        onSuccess: () => {
          setCredentialsDraft({});
          setEditingPassword(false);
          toast.success("Identifiants mis à jour");
        },
      },
    );
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Connexion robot</h2>
          <p className="mt-1.5 text-sm text-gray-500">Compte utilisé par les robots pour enregistrer les dossiers dans l'application du laboratoire.</p>
        </div>
        <Link
          to="/machine"
          className="flex h-8 shrink-0 items-center gap-1 rounded-md bg-white pl-3 pr-2 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50"
        >
          Voir les machines
          <LuArrowUpRight className="size-3.5 text-gray-400" />
        </Link>
      </div>

      <div className="mb-10 rounded-xl bg-gray-100 px-4 py-3.5">
        <p className="text-[13px] font-medium text-gray-900">Changer le mot de passe sans bloquer les robots</p>
        <ol role="list" className="mt-2.5 flex flex-col gap-2 text-[13px] text-gray-600">
          <li className="flex items-center gap-3">
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[11px] font-medium tabular-nums text-gray-600 ring-1 ring-gray-950/10">1</span>
            Activer « Robots en pause ».
          </li>
          <li className="flex items-center gap-3">
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[11px] font-medium tabular-nums text-gray-600 ring-1 ring-gray-950/10">2</span>
            Modifier le mot de passe dans l'application du laboratoire.
          </li>
          <li className="flex items-center gap-3">
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[11px] font-medium tabular-nums text-gray-600 ring-1 ring-gray-950/10">3</span>
            Enregistrer le nouveau mot de passe ci-dessous.
          </li>
          <li className="flex items-center gap-3">
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[11px] font-medium tabular-nums text-gray-600 ring-1 ring-gray-950/10">4</span>
            Désactiver « Robots en pause ».
          </li>
        </ol>
      </div>

      <h3 className="mb-3 text-[15px] font-medium text-gray-900">Activité</h3>
      <div className="overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
        <div className="flex items-center justify-between gap-6 px-4 py-3.5">
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-gray-900">Robots en pause</p>
            <p className={`mt-0.5 text-[13px] ${setting.botsFrozen ? "text-amber-700" : "text-gray-500"}`}>
              {setting.botsFrozen
                ? "Les robots ne traitent plus aucune admission. Pensez à reprendre une fois le mot de passe mis à jour."
                : "Suspend la distribution des admissions aux robots."}
            </p>
          </div>
          <span className="relative inline-flex h-5 w-9 shrink-0 rounded-full bg-gray-200 p-0.5 transition-colors has-[:checked]:bg-amber-500 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2">
            <input
              type="checkbox"
              role="switch"
              name="botsFrozen"
              aria-label="Robots en pause"
              className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-full focus:outline-none disabled:cursor-not-allowed"
              checked={setting.botsFrozen}
              disabled={settingMutation.isPending}
              onChange={(e) => settingMutation.mutate({ botsFrozen: e.target.checked })}
            />
            <span className="pointer-events-none size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
          </span>
        </div>
      </div>

      <h3 className="mb-3 mt-10 text-[15px] font-medium text-gray-900">Identifiants</h3>
      <form onSubmit={onSaveCredentials} className="overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
        <div className="divide-y divide-gray-100 px-4">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
            <label htmlFor="bot-url" className="text-[13px] font-medium text-gray-900">
              URL de l'application
            </label>
            <input
              id="bot-url"
              name="url"
              type="url"
              placeholder="https://..."
              value={credentialsDraft.url ?? credentials.url}
              onChange={(e) => setCredentialsDraft({ ...credentialsDraft, url: e.target.value })}
              className="h-8 w-80 max-w-full rounded-md border border-transparent bg-gray-100 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-200/70 focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
            <label htmlFor="bot-username" className="text-[13px] font-medium text-gray-900">
              Identifiant
            </label>
            <input
              id="bot-username"
              name="username"
              type="text"
              value={credentialsDraft.username ?? credentials.username}
              onChange={(e) => setCredentialsDraft({ ...credentialsDraft, username: e.target.value })}
              className="h-8 w-80 max-w-full rounded-md border border-transparent bg-gray-100 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-200/70 focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
            <div>
              <label htmlFor="bot-password" className="text-[13px] font-medium text-gray-900">
                Mot de passe
              </label>
              {credentials.lastSetAt && (
                <p className="mt-0.5 text-[13px] tabular-nums text-gray-500">
                  Modifié le {new Date(credentials.lastSetAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              )}
            </div>
            {editingPassword || !credentials.hasPassword ? (
              <div className="flex w-80 max-w-full items-center gap-2">
                <input
                  id="bot-password"
                  name="password"
                  type="password"
                  autoFocus={editingPassword}
                  autoComplete="new-password"
                  placeholder="Nouveau mot de passe"
                  value={credentialsDraft.password ?? ""}
                  onChange={(e) => setCredentialsDraft({ ...credentialsDraft, password: e.target.value })}
                  className="h-8 min-w-0 flex-1 rounded-md border border-transparent bg-gray-100 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-200/70 focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20"
                />
                {editingPassword && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPassword(false);
                      setCredentialsDraft({ ...credentialsDraft, password: "" });
                    }}
                    className="h-8 shrink-0 rounded-md px-2 text-[13px] text-gray-500 transition-colors hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                )}
              </div>
            ) : (
              <div className="flex w-80 max-w-full items-center justify-between gap-2">
                <p className="font-mono text-[13px] tracking-widest text-gray-400">••••••••••</p>
                <button
                  type="button"
                  onClick={() => setEditingPassword(true)}
                  className="h-7 rounded-md bg-white px-2.5 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50"
                >
                  Modifier
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
            <div>
              <label htmlFor="bot-username-id" className="text-[13px] font-medium text-gray-900">
                ID de l'identifiant
              </label>
              <p className="mt-0.5 text-[13px] text-gray-500">Valeur du champ loginSelect de la page de connexion.</p>
            </div>
            <input
              id="bot-username-id"
              name="usernameId"
              type="text"
              value={credentialsDraft.usernameId ?? credentials.usernameId}
              onChange={(e) => setCredentialsDraft({ ...credentialsDraft, usernameId: e.target.value })}
              className="h-8 w-80 max-w-full rounded-md border border-transparent bg-gray-100 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-200/70 focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/70 px-4 py-3">
          <p className="text-[13px] text-gray-500">Les robots utilisent ces identifiants dès le prochain dossier.</p>
          <button
            type="submit"
            disabled={settingMutation.isPending || Object.keys(changedCredentials).length === 0}
            className="h-8 shrink-0 rounded-md bg-primary-500 px-3 text-[13px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(0,0,0,0.12)] transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
};

export default RobotSection;
